import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { formatDuration } from '@/lib/format-duration';
import { courseTotalActiveSeconds, formatCourseProgressPercent } from '@/lib/course-duration';
import { roleLabel } from '@/lib/role-display';
import { parseCustomFieldMediaIds } from '@/lib/custom-field-display';
import { extractUserRoleSlugs, normalizeUserRoles } from '@/lib/user-roles';
import { UserShowMediaField } from '@/features/users/UserShowMediaField';
import {
  formatDateTimeEs,
  formatUserCustomFieldValue,
  isCustomFieldMediaType,
  isPersonalOrGeneralGroup,
  relativeTimeEs,
  userRoleIcon,
  userShowFieldSections,
  userStatusLabel,
  type UserShowCustomField,
} from '@/features/users/user-show-utils';

interface ConnectionTimeCourseSummary {
  academicHours: number;
  activeSeconds: number;
}

interface ConnectionTimeSummary {
  totalActiveSeconds: number;
  byCourse?: ConnectionTimeCourseSummary[];
}

interface UserContractRow {
  id: string;
  code: string;
  status: string;
  courseName: string;
  templateTitle: string;
}

interface PolicyAcceptanceRow {
  id: string;
  policyTitle: string;
  acceptedAt: string;
  source: string;
}

interface EnrolledCourseRow {
  id: string;
  name: string;
  progressPercent: number;
}

export interface ProfileDetail {
  id: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string;
  email: string;
  phone?: string | null;
  roles?: unknown;
  status?: string;
  createdAt?: string;
  lastLoginAt?: string | null;
  avatarUrl?: string | null;
  emailVerifiedAt?: string | null;
  trashed?: boolean;
  personalCustomFields?: UserShowCustomField[];
  customFieldSections?: { groupName: string; fields: UserShowCustomField[] }[];
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  pending_acceptance: 'Pendiente',
  accepted: 'Aceptado',
  generated: 'Generado',
};

function contractStatusModifier(status: string): string {
  if (status === 'accepted') return 'lms-user-show-status--accepted';
  if (status === 'pending_acceptance') return 'lms-user-show-status--pending';
  return 'lms-user-show-status--default';
}

function policyAcceptanceSourceLabel(source: string): string {
  switch (source) {
    case 'contract_signing':
      return 'Firma de contrato';
    case 'register':
      return 'Registro';
    default:
      return 'Aceptación directa';
  }
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase();
}

function foldName(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function groupIcon(groupName: string) {
  const name = foldName(groupName);
  if (name.includes('comercial') || name.includes('pago') || name.includes('caja')) return 'payments';
  if (name.includes('academ')) return 'school';
  if (isPersonalOrGeneralGroup(groupName)) return 'person';
  return 'folder';
}

function isWideField(field: UserShowCustomField) {
  const type = field.type.toLowerCase();
  const key = foldName(`${field.slug} ${field.name}`);
  return type === 'textarea' || key.includes('direccion') || key.includes('address') || key.includes('observ');
}

function isSelectLike(type: string) {
  const normalized = type.toLowerCase();
  return normalized === 'select' || normalized === 'radio';
}

function fieldValueClass(field: UserShowCustomField, empty: boolean) {
  if (empty) return 'lms-user-show-field__value lms-user-show-field__value--empty';
  const type = field.type.toLowerCase();
  if (type === 'date' || type === 'datetime') return 'lms-user-show-field__value lms-user-show-field__value--datetime';
  if (isSelectLike(type)) return 'lms-user-show-field__value lms-user-show-field__value--select';
  return 'lms-user-show-field__value';
}

function rolePillClass(role: string) {
  if (role === 'super-admin' || role === 'admin') return 'lms-user-show-pill lms-user-show-pill--role lms-user-show-pill--role-admin';
  if (role === 'teacher') return 'lms-user-show-pill lms-user-show-pill--role lms-user-show-pill--role-teacher';
  return 'lms-user-show-pill lms-user-show-pill--role';
}

function statusPillClass(status?: string) {
  if (status === 'inactive') return 'lms-user-show-pill lms-user-show-pill--inactive';
  if (status === 'deleted') return 'lms-user-show-pill lms-user-show-pill--deleted';
  return 'lms-user-show-pill lms-user-show-pill--active';
}

function ShowCard({
  icon,
  title,
  hint,
  count,
  children,
  flush,
}: {
  icon: string;
  title: string;
  hint?: string | null;
  count?: string;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="lms-user-show-card">
      <div className="lms-user-show-card__head">
        <div className="lms-user-show-card__head-title">
          <span className="material-symbols-outlined" aria-hidden="true">
            {icon}
          </span>
          <h3 className="lms-user-show-card__title">{title}</h3>
        </div>
        {count ? <span className="lms-user-show-card__count">{count}</span> : null}
        {!count && hint ? <span className="lms-user-show-card__hint">{hint}</span> : null}
      </div>
      <div className={flush ? 'lms-user-show-card__body lms-user-show-card__body--flush' : 'lms-user-show-card__body'}>
        {children}
      </div>
    </section>
  );
}

function CustomFieldValue({ field }: { field: UserShowCustomField }) {
  const rendered = formatUserCustomFieldValue(field);
  const empty = rendered === '—';
  const type = field.type.toLowerCase();
  return (
    <span className={fieldValueClass(field, empty)}>
      {!empty && (type === 'date' || type === 'datetime') ? (
        <span className="material-symbols-outlined" aria-hidden="true">
          calendar_month
        </span>
      ) : null}
      {!empty && isSelectLike(type) ? (
        <span className="material-symbols-outlined" aria-hidden="true">
          badge
        </span>
      ) : null}
      {rendered}
    </span>
  );
}

function fieldHasMediaFiles(field: UserShowCustomField) {
  return isCustomFieldMediaType(field.type) && parseCustomFieldMediaIds(field.value ?? field.rawValue).length > 0;
}

function FieldGrid({ fields, columns }: { fields: UserShowCustomField[]; columns: 2 | 3 }) {
  const textFields = fields.filter((field) => !fieldHasMediaFiles(field));
  const mediaFields = fields.filter((field) => fieldHasMediaFiles(field));

  return (
    <>
      {textFields.length > 0 ? (
        <div className={`lms-user-show-fields lms-user-show-fields--${columns}`}>
          {textFields.map((field) => (
            <div
              key={field.id}
              className={`lms-user-show-field${isWideField(field) ? ' lms-user-show-field--wide' : ''}${
                columns === 3 && isSelectLike(field.type) ? ' lms-user-show-field--span-2' : ''
              }`}
            >
              <span className="lms-user-show-field__label">{field.name}</span>
              <CustomFieldValue field={field} />
            </div>
          ))}
        </div>
      ) : null}
      {mediaFields.map((field) => (
        <UserShowMediaField key={field.id} field={field} />
      ))}
    </>
  );
}

export function ProfileOverview({
  backTo,
  onEdit,
  onPassword,
}: {
  backTo: string;
  onEdit: () => void;
  onPassword: () => void;
}) {
  const { hasPermission } = usePermissions();
  const canViewConnectionDetail = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR);
  const canViewContracts = hasPermission(PERMISSIONS.CONTRATOS_VER);
  const canViewPolicies = hasPermission(PERMISSIONS.POLITICAS_LEGALES_VER);
  const canViewProgress = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR);

  const { data, isLoading, error } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const user = await api<ProfileDetail>('/users/me');
      return normalizeUserRoles(user);
    },
  });

  const userId = data?.id;

  const { data: connectionTime, isLoading: isConnectionTimeLoading } = useQuery({
    queryKey: ['profile', 'me', 'connection-time'],
    queryFn: () => api<ConnectionTimeSummary>('/users/me/connection-time'),
    enabled: Boolean(userId),
  });

  const { data: contractsData, isLoading: isContractsLoading } = useQuery({
    queryKey: ['profile', 'me', 'contracts'],
    queryFn: () =>
      listResource<UserContractRow>('/contracts', {
        page: 1,
        perPage: 100,
        userId: userId!,
      }),
    enabled: Boolean(userId) && canViewContracts,
  });

  const { data: policyAcceptancesData, isLoading: isPoliciesLoading } = useQuery({
    queryKey: ['profile', 'me', 'policy-acceptances'],
    queryFn: () =>
      api<{ data: PolicyAcceptanceRow[] }>(`/legal/policies/acceptances?userId=${userId}&page=1&perPage=100`),
    enabled: Boolean(userId) && canViewPolicies,
  });

  const { data: enrolledCoursesData, isLoading: isEnrolledCoursesLoading } = useQuery({
    queryKey: ['profile', 'me', 'enrolled-courses'],
    queryFn: () => api<{ courses: EnrolledCourseRow[] }>('/users/me/enrolled-courses'),
    enabled: Boolean(userId),
  });

  const displayName = data?.fullName ?? data?.name ?? 'Usuario';
  const roleSlugs = extractUserRoleSlugs(data?.roles);
  const fieldSections = userShowFieldSections(
    data?.personalCustomFields ?? [],
    data?.customFieldSections ?? [],
    data?.phone,
  );
  const contracts = contractsData?.data ?? [];
  const policyAcceptances = policyAcceptancesData?.data ?? [];
  const showContractsAndPolicies = canViewContracts || canViewPolicies;
  const isContractsAndPoliciesLoading =
    (canViewContracts && isContractsLoading) || (canViewPolicies && isPoliciesLoading);
  const enrolledCourses = enrolledCoursesData?.courses ?? [];
  const connectionCourses = connectionTime?.byCourse ?? [];
  const singleConnectionCourse = connectionCourses.length === 1 ? connectionCourses[0] : null;
  const totalActiveSeconds = connectionTime?.totalActiveSeconds ?? 0;
  const singleCourseTotalSeconds = singleConnectionCourse
    ? courseTotalActiveSeconds(singleConnectionCourse.academicHours)
    : null;
  const singleCourseProgressLabel =
    singleCourseTotalSeconds != null
      ? formatCourseProgressPercent(totalActiveSeconds, singleCourseTotalSeconds)
      : null;
  const courseCountLabel = enrolledCourses.length === 1 ? '1 curso' : `${enrolledCourses.length} cursos`;

  return (
    <div className="lms-site-admin-hub-page lms-user-show font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to={backTo} label="Volver" />
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Mi perfil</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--us-primary,#000256)] bg-white px-4 py-2 text-xs font-semibold text-[var(--us-primary,#000256)]"
            onClick={onPassword}
          >
            <span className="material-symbols-outlined text-base" aria-hidden="true">
              lock
            </span>
            Contraseña
          </button>
          <button type="button" className="lms-user-show__edit" onClick={onEdit}>
            <span className="material-symbols-outlined" aria-hidden="true">
              edit
            </span>
            Editar perfil
          </button>
        </div>
      </div>

      {error ? (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="lms-user-show-loader">
          <Loader />
        </div>
      ) : (
        <>
          <div className="lms-user-show__profile">
            <div className="lms-user-show__identity">
              {data?.avatarUrl ? (
                <img src={data.avatarUrl} alt="" className="lms-user-show__avatar-img" />
              ) : (
                <div className="lms-user-show__avatar">{initials(displayName)}</div>
              )}
              <div>
                <div className="lms-user-show__name-row">
                  <h2 className="lms-user-show__name">{displayName}</h2>
                  <span className={statusPillClass(data?.trashed ? 'deleted' : data?.status)}>
                    <span className="lms-user-show-pill__dot" aria-hidden="true" />
                    {data?.trashed ? 'Eliminado' : userStatusLabel(data?.status)}
                  </span>
                  {roleSlugs.map((role) => (
                    <span key={role} className={rolePillClass(role)}>
                      <span className="material-symbols-outlined" aria-hidden="true">
                        {userRoleIcon(role)}
                      </span>
                      {roleLabel(role)}
                    </span>
                  ))}
                </div>
                <p className="lms-user-show__email">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    mail
                  </span>
                  {data?.email}
                </p>
              </div>
            </div>

            <div className="lms-user-show__kpi">
              <div className="lms-user-show__kpi-icon">
                <span className="material-symbols-outlined" aria-hidden="true">
                  timer
                </span>
              </div>
              <div>
                <div className="lms-user-show__kpi-label">Horas de conexión</div>
                {isConnectionTimeLoading ? (
                  <Loader />
                ) : (
                  <div className="lms-user-show__kpi-value">
                    {formatDuration(totalActiveSeconds)}
                    {singleCourseProgressLabel ? (
                      <span className="lms-user-show__kpi-pct">/ {singleCourseProgressLabel}</span>
                    ) : null}
                  </div>
                )}
              </div>
              {canViewConnectionDetail && userId ? (
                <Link to={`/progreso/tiempo-conexion/${userId}`} className="lms-user-show__kpi-link">
                  Ver detalle de sesiones
                  <span className="material-symbols-outlined" aria-hidden="true">
                    arrow_forward
                  </span>
                </Link>
              ) : null}
            </div>
          </div>

          <div className="lms-user-show__grid">
            <div className="lms-user-show__col">
              {fieldSections.map((section, index) => (
                <ShowCard key={`${section.groupName}-${index}`} icon={groupIcon(section.groupName)} title={section.groupName}>
                  <FieldGrid columns={3} fields={section.fields} />
                </ShowCard>
              ))}

              {showContractsAndPolicies ? (
                <ShowCard icon="gavel" title="Contrato y Políticas Legales">
                  {isContractsAndPoliciesLoading ? (
                    <div className="lms-user-show-loader">
                      <Loader />
                    </div>
                  ) : (
                    <>
                      {canViewContracts ? (
                        <div className="lms-user-show-block">
                          <div className="lms-user-show-block__label">Contratos</div>
                          {contracts.length === 0 ? (
                            <p className="lms-user-show-card__empty">Sin contratos generados.</p>
                          ) : (
                            <div className="space-y-3">
                              {contracts.map((contract) => (
                                <div key={contract.id} className="lms-user-show-row">
                                  <div className="lms-user-show-row__main">
                                    <div className="lms-user-show-row__icon">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        description
                                      </span>
                                    </div>
                                    <div>
                                      <p className="lms-user-show-row__title">
                                        {contract.templateTitle || contract.courseName}
                                      </p>
                                      <p className="lms-user-show-row__meta">{contract.code}</p>
                                    </div>
                                  </div>
                                  <div className="lms-user-show-row__actions">
                                    <span className={`lms-user-show-status ${contractStatusModifier(contract.status)}`}>
                                      <span className="lms-user-show-pill__dot" aria-hidden="true" />
                                      {CONTRACT_STATUS_LABELS[contract.status] ?? contract.status}
                                    </span>
                                    <Link to={`/contratos/${contract.id}`} className="lms-user-show-btn">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        visibility
                                      </span>
                                      Ver contrato
                                    </Link>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}

                      {canViewPolicies ? (
                        <div className="lms-user-show-block">
                          <div className="lms-user-show-block__label">Políticas legales</div>
                          {policyAcceptances.length === 0 ? (
                            <p className="lms-user-show-card__empty">Sin políticas aceptadas.</p>
                          ) : (
                            <div className="space-y-3">
                              {policyAcceptances.map((acceptance) => (
                                <div key={acceptance.id} className="lms-user-show-row lms-user-show-row--policy">
                                  <div className="lms-user-show-row__main">
                                    <div className="lms-user-show-row__icon lms-user-show-row__icon--muted">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        policy
                                      </span>
                                    </div>
                                    <div>
                                      <p className="lms-user-show-row__title">{acceptance.policyTitle}</p>
                                      <p className="lms-user-show-row__meta lms-user-show-row__meta--plain">
                                        <span className="lms-user-show-row__source">
                                          {policyAcceptanceSourceLabel(acceptance.source)}
                                        </span>
                                        <span aria-hidden="true">•</span>
                                        <span>{formatDateTimeEs(acceptance.acceptedAt)}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <span className="lms-user-show-status lms-user-show-status--accepted">
                                    <span className="lms-user-show-pill__dot" aria-hidden="true" />
                                    Aceptado
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </ShowCard>
              ) : null}
            </div>

            <div className="lms-user-show__col">
              <ShowCard
                icon="auto_stories"
                title="Cursos Inscritos"
                count={isEnrolledCoursesLoading ? undefined : courseCountLabel}
              >
                {isEnrolledCoursesLoading ? (
                  <div className="lms-user-show-loader">
                    <Loader />
                  </div>
                ) : enrolledCourses.length === 0 ? (
                  <p className="lms-user-show-card__empty">Sin cursos inscritos.</p>
                ) : (
                  enrolledCourses.map((course) => {
                    const progress = Math.max(0, Math.min(100, course.progressPercent));
                    return (
                      <div key={course.id} className="lms-user-show-course">
                        <div className="lms-user-show-course__top">
                          {canViewProgress && userId ? (
                            <Link to={`/progreso/${userId}/${course.id}`} className="lms-user-show-course__name">
                              {course.name}
                            </Link>
                          ) : (
                            <p className="lms-user-show-course__name">{course.name}</p>
                          )}
                          <span className="lms-user-show-course__pct">{progress}%</span>
                        </div>
                        <p className="lms-user-show-course__desc">
                          Progreso de actividades y tiempo activo en plataforma.
                        </p>
                        <div className="lms-user-show-course__bar">
                          <div className="lms-user-show-course__bar-fill" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </ShowCard>

              <ShowCard icon="settings_suggest" title="Información del Sistema">
                <div className="lms-user-show-system">
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Registrado</span>
                    <span className="lms-user-show-system__value">{formatDateTimeEs(data?.createdAt) ?? '—'}</span>
                  </div>
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Último acceso</span>
                    <div className="lms-user-show-system__value">
                      {data?.lastLoginAt ? (
                        <>
                          <div>{formatDateTimeEs(data.lastLoginAt)}</div>
                          <div className="lms-user-show-system__rel">{relativeTimeEs(data.lastLoginAt)}</div>
                        </>
                      ) : (
                        'Nunca'
                      )}
                    </div>
                  </div>
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Email verificado</span>
                    {data?.emailVerifiedAt ? (
                      <span className="lms-user-show-verified">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          check_circle
                        </span>
                        {formatDateTimeEs(data.emailVerifiedAt)}
                      </span>
                    ) : (
                      <span className="lms-user-show-pending">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          pending
                        </span>
                        Pendiente
                      </span>
                    )}
                  </div>
                </div>
              </ShowCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
